import axios from "axios";
import { renderHtmlToPdfWithFields } from "./signaturesHtmlToPdf";
import type { HtmlToPdfResult } from "./signaturesHtmlToPdf";

/**
 * Preparing a contract ("הכנת חוזה") around the canonical compiler.
 *
 * Measured in production (Cloud Run request logs, 2026-08-31 → 2026-10-19,
 * `adHocOperations/measure-signature-preparation-latency.ts` in the server repo):
 * the compile is the wait. A contract compiles in ~5 s (p50), an addresses
 * addendum in ~2.3 s, and a preparation that reused the preview's compilation
 * finished in 1.4 s — against 7.2 s with one compile, 12.5 s with a contract
 * plus its addendum and 12.7 s for a seller + exclusivity pair, because the
 * documents of one preparation were compiled one after the other. (Side by side
 * they still share a 1-vCPU instance when they land on the same one: ~1.6x each,
 * so a pair saves ~2 s there, more when the two land on different instances.)
 *
 * So the form compiles the exact HTML it will send as soon as the form is ready
 * (`createPrepareAhead`), and a preparation compiles its documents side by side
 * (`compileDocuments`). Both go through `renderHtmlToPdfWithFields`, whose
 * in-memory cache is keyed by the resolved HTML: the send reuses a compilation
 * only when its HTML is byte-identical, so what is archived is exactly what was
 * compiled for it. Nothing here stores, numbers or sends a document.
 */

/** Typical durations (production p50s) behind the progress estimate. Approximate by design. */
export const PREPARATION_TIMINGS = {
  /** POST /documents (draft + number): 0.15 s on the server, plus the round trip. */
  numberMs: 400,
  /** POST /render of a contract. */
  compileMs: 5000,
  /** POST /render of an addresses addendum. */
  addendumCompileMs: 2500,
  /** Two compiles sharing one 1-vCPU instance most of their time: ~8.0 s each against 4.8 s alone (p50). */
  parallelSlowdown: 1.6,
  /** Upload of the compiled PDF (p50 0.8 s) + POST /send (p50 0.8 s). */
  saveMs: 1600,
  /** A bundle uploads two PDFs in one request. */
  bundleSaveMs: 2000
};

/** Compiles in flight at once from one preparation. The server runs two per instance. */
export const PREPARATION_COMPILE_CONCURRENCY = 2;

/** Compile ahead of the click while the form is ready to send. Off: compile on click only. */
export const SIGNATURE_PREPARE_AHEAD_ENABLED = true;
/** Quiet time after the last change before compiling ahead (typing a fee is several changes). */
export const PREPARE_AHEAD_DELAY_MS = 1200;
/** Rounds of compiling ahead per open form; past it, the click compiles as before. */
export const PREPARE_AHEAD_MAX_ROUNDS = 8;

// After renderHtmlToPdfWithFields' own two 429 retries (2 s + 4 s), wait for the
// renderer a while longer before failing a preparation the realtor is watching.
const BUSY_RETRY_DELAYS_MS = [3000, 5000, 8000];

interface CompileTiming {
  startedAt: number;
  settledAt?: number;
  failed?: boolean;
}

// Start/finish times of the compilations this tab asked for, keyed like the
// compile cache (resolved HTML). Only the progress estimate reads it.
const timings = new Map<string, CompileTiming>();
const TIMING_LIMIT = 16;
// A little under the compile cache's 10 minutes: an expired cache entry compiles again.
const TIMING_TTL_MS = 9 * 60 * 1000;

function timingOf(html: string, now = Date.now()): CompileTiming | undefined {
  const timing = timings.get(html);
  if (timing && now - timing.startedAt > TIMING_TTL_MS) {
    timings.delete(html);
    return undefined;
  }
  return timing;
}

function isRendererBusy(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** One compilation through the shared cache, timed for the progress estimate. */
export function compileDocument(html: string): Promise<HtmlToPdfResult> {
  const now = Date.now();
  const known = timingOf(html, now);
  if (!known || known.failed) {
    timings.delete(html);
    timings.set(html, { startedAt: now });
    while (timings.size > TIMING_LIMIT) timings.delete(timings.keys().next().value!);
  }
  const timing = timings.get(html)!;
  const result = renderHtmlToPdfWithFields(html);
  result.then(
    () => { if (timings.get(html) === timing && !timing.settledAt) timing.settledAt = Date.now(); },
    () => { if (timings.get(html) === timing) timing.failed = true; }
  );
  return result;
}

async function compileWithBusyRetries(html: string, onBusy?: () => void): Promise<HtmlToPdfResult> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await compileDocument(html);
    } catch (error) {
      if (attempt >= BUSY_RETRY_DELAYS_MS.length || !isRendererBusy(error)) throw error;
      onBusy?.();
      await wait(BUSY_RETRY_DELAYS_MS[attempt]);
    }
  }
}

/**
 * Compile every HTML, at most `concurrency` at once, keeping input order.
 * Duplicates share one compilation. The first failure rejects at once (the
 * realtor is told now, not after the other compile ends); compilations already
 * running are left to finish into the cache, where a retry finds them.
 */
export function compileDocuments(
  htmls: string[],
  options: { concurrency?: number; onCompiled?: (index: number) => void; onBusy?: () => void } = {}
): Promise<HtmlToPdfResult[]> {
  const results: HtmlToPdfResult[] = new Array(htmls.length);
  if (htmls.length === 0) return Promise.resolve(results);
  const width = Math.max(1, Math.min(options.concurrency ?? PREPARATION_COMPILE_CONCURRENCY, htmls.length));
  return new Promise<HtmlToPdfResult[]>((resolve, reject) => {
    let next = 0;
    let done = 0;
    let failed = false;
    const launch = (): void => {
      if (failed || next >= htmls.length) return;
      const index = next;
      next += 1;
      compileWithBusyRetries(htmls[index], options.onBusy).then(
        (result) => {
          if (failed) return;
          results[index] = result;
          done += 1;
          options.onCompiled?.(index);
          if (done === htmls.length) resolve(results);
          else launch();
        },
        (error) => {
          if (failed) return;
          failed = true;
          reject(error);
        }
      );
    };
    for (let slot = 0; slot < width; slot += 1) launch();
  });
}

/** Whether this exact HTML has already compiled successfully in this tab. */
export function isCompiled(html: string): boolean {
  const timing = timingOf(html);
  return !!timing?.settledAt && !timing.failed;
}

/**
 * Compile ahead of the click: the latest ready set of documents, one round at a
 * time (a newer set waits for the round in flight and replaces any older one
 * waiting), one compile at a time, never the same set twice, at most
 * `maxRounds` rounds per form. Failures are dropped — the click compiles again
 * and reports the error.
 */
export function createPrepareAhead(options: { maxRounds?: number } = {}) {
  const maxRounds = options.maxRounds ?? PREPARE_AHEAD_MAX_ROUNDS;
  let rounds = 0;
  let running = false;
  let waiting: string[] | null = null;
  let lastKey = "";
  let stopped = false;

  const keyOf = (htmls: string[]) => htmls.join("\u0000");

  const run = (htmls: string[]) => {
    rounds += 1;
    running = true;
    lastKey = keyOf(htmls);
    // One at a time: nobody is waiting yet, so leave the instance's second
    // renderer slot to whoever is. A click joins whatever is in flight and
    // compiles the rest itself, two at a time.
    compileDocuments(htmls, { concurrency: 1 })
      .catch(() => {
        // Let the same set be tried again (a busy renderer is not a verdict).
        lastKey = "";
      })
      .finally(() => {
        running = false;
        const queued = waiting;
        waiting = null;
        if (queued && !stopped) request(queued);
      });
  };

  function request(htmls: string[]): void {
    if (stopped || htmls.length === 0) return;
    if (running) {
      waiting = htmls;
      return;
    }
    if (keyOf(htmls) === lastKey || htmls.every(isCompiled)) return;
    if (rounds >= maxRounds) return;
    run(htmls);
  }

  return {
    request,
    /** The form is closing or preparing: drop what is waiting (a round in flight still lands in the cache). */
    stop: () => {
      stopped = true;
      waiting = null;
    },
    get rounds() {
      return rounds;
    }
  };
}

// ---------------------------------------------------------------------------
// Progress shown while the realtor waits (accepted → step → rough time left).
// ---------------------------------------------------------------------------

export type PreparationKind = "single" | "bundle" | "edit";
export type PreparationStep = "number" | "compile" | "save";

export interface PreparationProgress {
  kind: PreparationKind;
  /** Contracts in this preparation (a bundle's members); addenda are not counted. */
  documentCount: number;
  steps: PreparationStep[];
  step: PreparationStep;
  /** When the click was accepted. */
  acceptedAt: number;
  /** Estimated end of the whole preparation; re-estimated at every step. */
  expectedEndAt: number;
  /** Contracts whose PDF is ready. */
  compiledCount: number;
  /** The renderer answered "busy" and the compile is being retried. */
  retrying?: boolean;
}

/** One item the compile step needs: its HTML and whether it is an addendum (shorter). */
export interface CompileItem {
  html: string;
  addendum?: boolean;
}

/** Estimated time until these compilations are all done, given what already ran. */
export function estimateCompileRemainingMs(items: CompileItem[], now = Date.now()): number {
  const remaining = items
    .map((item) => {
      const full = item.addendum ? PREPARATION_TIMINGS.addendumCompileMs : PREPARATION_TIMINGS.compileMs;
      const timing = timingOf(item.html, now);
      if (!timing || timing.failed) return full;
      if (timing.settledAt) return 0;
      // In flight (compiled ahead, or started at the click): what is left of a typical compile.
      return Math.max(full * 0.15, full - (now - timing.startedAt));
    })
    .filter((ms) => ms > 0)
    .sort((a, b) => b - a);
  if (remaining.length === 0) return 0;
  if (remaining.length === 1) return remaining[0];
  // Two at a time, each a little slower for sharing the renderer.
  const total = remaining.reduce((sum, ms) => sum + ms, 0);
  return Math.max(remaining[0], total / PREPARATION_COMPILE_CONCURRENCY) * PREPARATION_TIMINGS.parallelSlowdown;
}

export function saveEstimateMs(kind: PreparationKind): number {
  return kind === "bundle" ? PREPARATION_TIMINGS.bundleSaveMs : PREPARATION_TIMINGS.saveMs;
}

/** The progress state at the click: every step still ahead. */
export function startPreparation(
  kind: PreparationKind,
  documentCount: number,
  items: CompileItem[],
  now = Date.now()
): PreparationProgress {
  const steps: PreparationStep[] = kind === "single" ? ["number", "compile", "save"] : ["compile", "save"];
  // The number is allocated while the predicted document already compiles.
  const numberMs = kind === "single" ? PREPARATION_TIMINGS.numberMs : 0;
  const compileMs = estimateCompileRemainingMs(items, now);
  return {
    kind,
    documentCount,
    steps,
    step: steps[0],
    acceptedAt: now,
    expectedEndAt: now + Math.max(numberMs, compileMs) + saveEstimateMs(kind),
    compiledCount: 0
  };
}

/** Move to the compile step with the documents actually being sent. */
export function enterCompileStep(progress: PreparationProgress, items: CompileItem[], now = Date.now()): PreparationProgress {
  return {
    ...progress,
    step: "compile",
    expectedEndAt: now + estimateCompileRemainingMs(items, now) + saveEstimateMs(progress.kind)
  };
}

export function enterSaveStep(progress: PreparationProgress, now = Date.now()): PreparationProgress {
  return {
    ...progress,
    step: "save",
    compiledCount: progress.documentCount,
    retrying: false,
    expectedEndAt: now + saveEstimateMs(progress.kind)
  };
}

export interface PreparationClock {
  /** 1-based position of the current step. */
  stepNumber: number;
  stepCount: number;
  /** Rounded seconds left; null once past the estimate. */
  secondsLeft: number | null;
  /** Past the estimate by a clear margin: say so, honestly. */
  overdue: boolean;
  /** 0–100 for a determinate bar; never 100 before the preparation actually ends. */
  percent: number;
}

/** What the waiting UI shows at `now`. */
export function readPreparationClock(progress: PreparationProgress, now = Date.now()): PreparationClock {
  const total = Math.max(1, progress.expectedEndAt - progress.acceptedAt);
  const elapsed = Math.max(0, now - progress.acceptedAt);
  const leftMs = progress.expectedEndAt - now;
  // Past the estimate the bar keeps creeping toward 95%, never claiming done.
  const ratio = elapsed <= total ? (elapsed / total) * 0.9 : 0.9 + 0.05 * (1 - total / elapsed);
  return {
    stepNumber: progress.steps.indexOf(progress.step) + 1,
    stepCount: progress.steps.length,
    secondsLeft: leftMs > 0 ? Math.max(1, Math.round(leftMs / 1000)) : null,
    overdue: leftMs < -Math.max(3000, total * 0.5),
    percent: Math.round(Math.min(0.95, Math.max(0.03, ratio)) * 100)
  };
}
